
import { jsx } from '@emotion/react';
import React, { useCallback, useEffect, useState } from 'react';
import { useData } from '../Data';
import { useCourseData } from './CourseData';
import { InsertHTML } from './InsertHTML';
import { coursePicture } from '../UzlDesign';

// a name as it can be compared, like person_key in teaching/courses.py: 'Möller' and 'Moller' are the same
const personKey = (name: string) =>
  (name ?? '').normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z]/g, '');

// Moodle's own accounts (guest, admin), which no teacher has: like MOODLE_SYSTEM_ACCOUNTS
const SYSTEM_ACCOUNTS = [1, 2];

// the team member who teaches under this Moodle account or name, as taught_by matches them for the person pages
function teamMember(users: any[], teacher: any) {
  return users.find((u) => u.moodleid && !SYSTEM_ACCOUNTS.includes(u.moodleid) && u.moodleid === teacher.uid)
    ?? users.find((u) => personKey(`${u.first_name ?? ''} ${u.last_name ?? ''}`) === personKey(teacher.name));
}

// the kinds of UnivIS entries (teaching/univis.py)
const KINDS: Record<string, [string, string]> = {
  'vorl': ['Vorlesung', 'Lecture'], 'v-ue': ['Vorlesung', 'Lecture'], 'ue': ['Übung', 'Exercise'],
  'sem': ['Seminar', 'Seminar'], 'pr': ['Praktikum', 'Lab course'], 'os': ['Oberseminar', 'Research seminar'],
  'pf': ['Prüfung', 'Exam'],
};
const kind = (type: string, en: boolean) => (KINDS[type] ?? ['Termin', 'Date'])[en ? 1 : 0];

// when and where, from UnivIS: one line per date, the exercise groups by their UnivIS name
function Dates({ title, items, en }: { title: string, items: any[], en: boolean }) {
  if (!items?.length) return null;
  return <>
    <h2>{title}</h2>
    <ul>{items.map((e: any, i: number) => (
      <li key={i}>
        <a href={e.link}>{e.type === 'pf' ? e.label.replace('Klausur', en ? 'Exam' : 'Klausur') : kind(e.type, en)}</a>
        {e.type === 'ue' && e.label ? ` (${e.label})` : ''}: {[e.when, (e.rooms ?? []).join(', ')].filter(Boolean).join(', ')}
      </li>
    ))}</ul>
  </>;
}

export function TeachingCourse(props: any) {
  const { current, setType, semesters, users, lang } = useData();
  const { courses } = useCourseData();
  const [current_semester, current_course] = current ?? [null, null];
  const course = courses.courses.find((c: any) => c.id == current_course) ?? null;

  useEffect(() => {
    setType(courses);
  }, [courses])
  if (!course) {
    return null;
  }
  // courses from the old website have no page left but the Internet Archive's: no link for them
  const link = course.origin !== 'archive' && !course.link?.includes('web.archive.org') ? course.link : null;
  return (
    <div>
      <h5>{ courses.name }</h5>
      <h1>{ course.title }</h1>
      <h3>{ course.cid }</h3>
      <div className="col3" style={{
        marginLeft: '12px',
        marginRight: 0,
        width: '240px',
        float: 'right',
      }}>
        { coursePicture(course) ?
          <img src={coursePicture(course)} width="150" height="auto" className="photo" style={{ marginLeft: 'auto', marginRight: 'auto' }} alt={course.name} title={course.name} />
          : ""}
        {link && <a href={link}><h2>Moodle</h2></a>}
        {course.univis?.link && <a href={course.univis.link}><h2>UnivIS</h2></a>}
        {course.teachers?.length > 0 && <>
          <h2>{lang === 'en' ? 'Lecturers' : 'Lehrende'}</h2>
          <ul>{course.teachers.map((t: any) => {
            // team members link to their page; teachers of other institutes stay a name
            const url = teamMember(users ?? [], t)?.[lang === 'en' ? 'url_en' : 'url_de'];
            // assistants come from the team's profiles (teaching/courses.py), Moodle names only its Dozent*innen
            const role = t.role === 'assistant' ? (lang === 'en' ? ' (assistant)' : ' (Assistenz)') : '';
            return <li key={t.uid ?? t.name}>{url ? <a href={url}>{t.name}</a> : t.name}{role}</li>;
          })}</ul>
        </>}
        <Dates title={lang === 'en' ? 'Dates' : 'Termine'} items={course.univis?.events} en={lang === 'en'} />
        <Dates title={lang === 'en' ? 'Exams' : 'Prüfungen'} items={course.univis?.exams} en={lang === 'en'} />
      </div>
      <InsertHTML model={course} field="summary" />
    </div>
  );
}